import React, { useState, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'
import LiveSitesTable from 'components/LiveSitesTable'
import ArchiveTable from 'components/ArchiveTable/ArchiveTable'
import DeletedTable from 'components/DeletedTable/DeletedTable'
import CustomDomain from 'components/CustomDomainTable'
import SidePanel from '../SidePanel/SidePanel'
import ErrorBoundary from 'components/ErrorBoundary/ErrorBoundary'
import { ErrorBox } from 'components/ErrorBox/ErrorBox'
import CreateSpaceModal from 'components/CreateSpaceModal/CreateSpaceModal'
import SpacesTabs, { EditSpaceModal, DeleteSpaceModal, AssignToSpaceModal } from 'components/SpacesTabs'
import { ACCOUNT_STATUS } from 'constants'
import styles from './ProjectViews.module.css'
import { isUserOnTinyPlan } from 'utils/general'

const ProjectViews = ({
  history,
  location,
  sites,
  archivedSites,
  userProfile,
  onDelete,
  onViewVersions,
  onDownload,
  onDownloadAll,
  onEdit,
  onSiteAction,
  onArchive,
  onAnalytics,
  trialAccount,
  onQRCode,
  onPreview,
  onEditPDF,
  onEmbed,
  onShare,
  onUpdate,
  onOpenTextEditor,
  onShowCodeEditor,
  onAddCustomDomain,
  siteLimit,
  connectGithub,
  viewRepos,
  onOpenGithubModal,
  onOpenTransferRequestLinkModal,
  onOpenUrl,
  onOpenDropBox,
  onDeleteArchive,
  onRestoreArchive,
  onRestoreDeleted,
  onPermanentlyDelete,
  dispatch,
  customDomains,
  refreshProfile,
  classification,
  onOpenEmailSubscription,
  onAnalyseSite,
  onSubmitGame,
  gameShowcaseStatuses
}) => {
  const spaces = useSelector((state) => state.manage.spaces || [])
  const [activeTab, setActiveTab] = useState('all')
  const [provisionalSpace, setProvisionalSpace] = useState(null)
  const [editSpaceModal, setEditSpaceModal] = useState({ show: false, spaceId: null, spaceName: '' })
  const [deleteSpaceModal, setDeleteSpaceModal] = useState({ show: false, spaceId: null, spaceName: '' })
  const [assignToSpaceModal, setAssignToSpaceModal] = useState({ show: false, site: null })
  const [hideSpacesProjects, setHideSpacesProjects] = useState(() => {
    const saved = localStorage.getItem('hideSpacesProjects')
    return saved === 'true'
  })

  // Clear provisional space when it appears in Redux spaces
  useEffect(() => {
    if (provisionalSpace && spaces.some((s) => s.id === provisionalSpace.id)) {
      setProvisionalSpace(null)
    }
  }, [spaces, provisionalSpace])

  const getActiveTab = () => {
    if (location.pathname === '/manage/archive') return 'archive'
    if (location.pathname === '/manage/deleted') return 'deleted'
    return activeTab
  }

  const onTabChange = (activeKey) => {
    if (activeKey === 'archive') {
      history.push('/manage/archive')
    } else if (activeKey === 'deleted') {
      history.push('/manage/deleted')
    } else {
      setActiveTab(activeKey)
      if (location.pathname !== '/manage') {
        history.push('/manage')
      }
    }
  }

  const handleEditSpace = (spaceId, spaceName) => {
    setEditSpaceModal({ show: true, spaceId, spaceName })
  }

  const handleDeleteSpace = (spaceId, spaceName) => {
    setDeleteSpaceModal({ show: true, spaceId, spaceName })
  }

  const handleDeleteSpaceSuccess = () => {
    setActiveTab('all')
    if (location.pathname !== '/manage') {
      history.push('/manage')
    }
  }

  const handleAssignToSpace = (site) => {
    setAssignToSpaceModal({ show: true, site })
  }

  const handleToggleHideSpacesProjects = (value) => {
    setHideSpacesProjects(value)
    localStorage.setItem('hideSpacesProjects', value.toString())
  }

  const tabs = useMemo(() => {
    const allSites = hideSpacesProjects ? sites.filter((site) => !site.spaceId) : sites

    let displaySpaces = [...spaces]
    if (provisionalSpace && !spaces.some((s) => s.id === provisionalSpace.id)) {
      displaySpaces.push(provisionalSpace)
    }

    const base = [
      {
        key: 'all',
        name: 'All',
        sites: allSites,
        count: allSites.length
      },
      ...displaySpaces.map((space) => ({
        key: String(space.spaceId),
        name: space.name,
        sites: sites.filter((site) => String(site.spaceId) === String(space.spaceId)),
        count: sites.filter((site) => String(site.spaceId) === String(space.spaceId)).length
      })),
      {
        key: 'archive',
        name: 'Archive',
        sites: archivedSites,
        count: archivedSites.length
      },
      {
        key: 'add-space',
        name: 'Add Space',
        isAddButton: true
      }
    ]
    return base
  }, [spaces, sites, archivedSites, hideSpacesProjects, provisionalSpace])

  const fallbackComponent = () => (
    <ErrorBox className="d-block" message={'Unable to load view<br /> please contact support'} hideButton={true} />
  )

  const getCurrentTabData = () => {
    const currentTab = getActiveTab()
    if (currentTab === 'deleted') {
      return { key: 'deleted', name: 'Deleted', sites: [], count: 0 }
    }
    return tabs.find((tab) => tab.key === currentTab) || tabs[0]
  }

  const currentTabData = getCurrentTabData()

  const showSidePanel = trialAccount || isUserOnTinyPlan(userProfile)

  return (
    <div className="flex">
      <div className="w-100">
        <div className="section">
          <SpacesTabs
            sites={sites}
            onTabChange={onTabChange}
            activeTab={getActiveTab()}
            siteLimit={siteLimit}
            hideSpacesProjects={hideSpacesProjects}
            onToggleHideSpacesProjects={handleToggleHideSpacesProjects}
          />

          <div className={styles.projectViewContainer} data-active-tab={getActiveTab()}>
            <ErrorBoundary fallback={fallbackComponent}>
              {currentTabData.key === 'archive' ? (
                <ArchiveTable
                  sites={archivedSites}
                  onDeleteArchive={onDeleteArchive}
                  onRestoreArchive={onRestoreArchive}
                />
              ) : currentTabData.key === 'deleted' ? (
                <DeletedTable onRestoreDeleted={onRestoreDeleted} onPermanentlyDelete={onPermanentlyDelete} />
              ) : (
                <LiveSitesTable
                  sites={currentTabData.sites}
                  userProfile={userProfile}
                  onDelete={onDelete}
                  onViewVersions={onViewVersions}
                  onDownload={onDownload}
                  onDownloadAll={onDownloadAll}
                  onEdit={onEdit}
                  onSiteAction={onSiteAction}
                  onArchive={onArchive}
                  onAnalytics={onAnalytics}
                  trialAccount={trialAccount}
                  onQRCode={onQRCode}
                  onPreview={onPreview}
                  onEditPDF={onEditPDF}
                  onEmbed={onEmbed}
                  onShare={onShare}
                  onOpenTextEditor={onOpenTextEditor}
                  onShowCodeEditor={onShowCodeEditor}
                  onAddCustomDomain={onAddCustomDomain}
                  badge={
                    currentTabData.key === 'all' && siteLimit !== 999
                      ? `${currentTabData.count} / ${siteLimit} live`
                      : null
                  }
                  connectGithub={connectGithub}
                  viewRepos={viewRepos}
                  onOpenGithubModal={onOpenGithubModal}
                  onOpenTransferRequestLinkModal={onOpenTransferRequestLinkModal}
                  onUpdate={onUpdate}
                  onOpenUrl={onOpenUrl}
                  onOpenDropBox={onOpenDropBox}
                  onSubmitGame={onSubmitGame}
                  gameShowcaseStatuses={gameShowcaseStatuses}
                  customDomains={customDomains}
                  currentSpaceName={currentTabData.name}
                  activeSpace={currentTabData.key}
                  onEditSpace={handleEditSpace}
                  onDeleteSpace={handleDeleteSpace}
                  onAssignToSpace={handleAssignToSpace}
                  hideSpacesProjects={hideSpacesProjects}
                  onToggleHideSpacesProjects={handleToggleHideSpacesProjects}
                  onAnalyseSite={onAnalyseSite}
                  noStyling={true}
                />
              )}
            </ErrorBoundary>
          </div>
        </div>
        <div className={`row ${styles.customDomainSection}`}>
          <CustomDomain
            customDomainsLimit={userProfile.customDomainsLimit}
            customDomains={customDomains}
            className="col-12 mt-5"
            dispatch={dispatch}
            productId={userProfile.productId}
            disabled={userProfile.status === ACCOUNT_STATUS.PAUSED}
            trialAccount={trialAccount}
            refreshProfile={refreshProfile}
            onSiteAction={onSiteAction}
            onOpenEmailSubscription={onOpenEmailSubscription}
          />
        </div>
      </div>
      <CreateSpaceModal
        onSpaceCreated={(space) => {
          setProvisionalSpace(space)
          setActiveTab(String(space.spaceId))
        }}
      />

      <EditSpaceModal
        isOpen={editSpaceModal.show}
        spaceId={editSpaceModal.spaceId}
        spaceName={editSpaceModal.spaceName}
        onClose={() => setEditSpaceModal({ show: false, spaceId: null, spaceName: '' })}
      />

      <DeleteSpaceModal
        isOpen={deleteSpaceModal.show}
        spaceId={deleteSpaceModal.spaceId}
        spaceName={deleteSpaceModal.spaceName}
        onClose={() => setDeleteSpaceModal({ show: false, spaceId: null, spaceName: '' })}
        onSuccess={handleDeleteSpaceSuccess}
      />

      <AssignToSpaceModal
        isOpen={assignToSpaceModal.show}
        site={assignToSpaceModal.site}
        onClose={() => setAssignToSpaceModal({ show: false, site: null })}
      />
      {showSidePanel && sites.length > 0 && (
        <SidePanel classification={classification} sites={sites} onAnalytics={onAnalytics} onQRCode={onQRCode} />
      )}
    </div>
  )
}

export default ProjectViews
